import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useTheme } from "@/contexts/ThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { Languages, Sun, Moon, SunMoon } from "lucide-react";

export function SettingsPage() {
  const { user } = useAuth();
  const { activeOrganization } = useOrganization();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Settings"
        description="Manage your profile, organization details, and workspace preferences."
      />

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">{t("Profile")}</TabsTrigger>
          <TabsTrigger value="organization">{t("Organization")}</TabsTrigger>
          <TabsTrigger value="appearance">{t("Appearance")}</TabsTrigger>
          <TabsTrigger value="language">{t("Language")}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>{t("Your profile")}</CardTitle>
              <CardDescription>{t("This is how you appear to your team and clients.")}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 pt-0">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="fullName">{t("Full name")}</Label>
                  <Input id="fullName" defaultValue={user?.fullName} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">{t("Email")}</Label>
                  <Input id="email" defaultValue={user?.email} disabled />
                </div>
              </div>
              <Separator />
              <div className="flex justify-end">
                <Button disabled>{t("Save changes")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle>{t("Organization details")}</CardTitle>
              <CardDescription>
                Visible to everyone invited to {activeOrganization?.name ?? "this organization"}.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 pt-0">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="orgName">{t("Organization name")}</Label>
                  <Input id="orgName" defaultValue={activeOrganization?.name} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="orgSlug">{t("Workspace URL")}</Label>
                  <Input id="orgSlug" defaultValue={activeOrganization?.slug} disabled />
                </div>
              </div>
              <Separator />
              <div className="flex justify-end">
                <Button disabled>{t("Save changes")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>{t("Appearance")}</CardTitle>
              <CardDescription>{t("Choose how Atelier Suite looks on this device.")}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {(
                  [
                    { value: "light", label: "Light", icon: Sun },
                    { value: "dark", label: "Dark", icon: Moon },
                    { value: "system", label: "System", icon: SunMoon },
                  ] as const
                ).map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTheme(option.value)}
                    className={cn(
                      "flex flex-col items-center gap-2 rounded-lg border border-border p-4 text-sm font-medium transition-colors hover:bg-muted",
                      theme === option.value && "border-primary bg-primary/5 text-primary"
                    )}
                  >
                    <option.icon className="h-5 w-5" />
                    {t(option.label)}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="language">
          <Card>
            <CardHeader><CardTitle>{t("Language preference")}</CardTitle><CardDescription>{t("Choose the language used throughout Atelier Suite.")}</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-1 gap-3 pt-0 sm:grid-cols-2">
              {([{ value: "en", label: "English" }, { value: "es", label: "Español" }] as const).map((option) => (
                <button key={option.value} onClick={() => setLanguage(option.value)} className={cn("flex items-center gap-3 rounded-lg border border-border p-4 text-left text-sm font-medium transition-colors hover:bg-muted", language === option.value && "border-primary bg-primary/5 text-primary")}>
                  <Languages className="h-5 w-5" />{option.label}
                  {language === option.value && <span className="ml-auto text-xs">{t("Active")}</span>}
                </button>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
